import clsx from "clsx";
import { MdAccountBalance } from "react-icons/md";
import React, { memo, useMemo } from "react";
import useMobile from "src/hooks/useMobile";
import { useTranslation } from "react-i18next";
import useCurrencies from "src/hooks/useCurrencies";
import useCurrentUser from "src/hooks/useCurrentUser";
import useUiPreferences from "src/store/useUiPreferences";
import { formatCurrency, truncateWithEllipsis } from "src/helpers/utils";
import SmartImage from "src/components/SmartImage/SmartImage";

const SWIPE_DELTA = 150;

interface IAutoBankCard {
  account: string;
  bank?: string;
  number?: string;
  name?: string;
  customStyles?: any;
  className?: string;
  currency: string;
  available?: any;
  disabled?: boolean;
  image?: any;
  onClick: (account: any) => void;
}

const AutoBankCard: React.FC<IAutoBankCard> = ({
  bank,
  number,
  name,
  account,
  customStyles = {},
  className,
  currency,
  available = null,
  disabled = false,
  image = null,
  onClick,
}) => {
  const { t } = useTranslation(["common"]);
  const accountsUiPreferences = useUiPreferences((s) => s.accounts);

  const { enabledAccountsList, allUserAccounts } = useCurrentUser();
  const { getCurrencyDataByCode } = useCurrencies();
  const { isMobile } = useMobile();

  const accountsToShow = useMemo(() => {
    if (accountsUiPreferences.showInactiveAccounts) {
      return allUserAccounts;
    }

    return enabledAccountsList;
  }, [
    accountsUiPreferences.showInactiveAccounts,
    allUserAccounts,
    enabledAccountsList,
  ]);

  const SHARED_STYLES = {
    relative: true,
    flex: true,
    "flex-col": true,
    "rounded md:rounded": true,
    "text-sm": true,
    "p-4": true,
    "gap-5": true,
    "transition-colors": true,
    group: true,
    // "shadow-md": true,
  };

  const currencyData = useMemo(() => {
    return getCurrencyDataByCode({ code: currency });
  }, [currency, getCurrencyDataByCode]);

  return (
    <>
      <div
        className={clsx([SHARED_STYLES, className])}
        style={{
          ...customStyles,
        }}
        onClick={() => {
          onClick(account);
        }}
      >
        <div
          className={clsx([
            "text-right",
            "flex",
            "flex-row",
            "gap-4",
            "justify-between",
            "items-center",
          ])}
        >
          {image ? (
            <div
              className={clsx([
                "inline-flex",
                "h-[90px]",
                "w-[90px]",
                "rounded",
              ])}
            >
              <SmartImage
                src={image}
                alt="Image"
                className={clsx([
                  "inline-flex",
                  "h-[90px]",
                  "w-[90px]",
                  "rounded",
                ])}
              />
            </div>
          ) : (
            <MdAccountBalance className={clsx([])} size={90} />
          )}
          <div className={clsx(["flex", "flex-col"])}>
            {bank && (
              <p
                className={clsx(["text-2xl", "font-normal", { italic: !bank }])}
              >
                {bank || t("pages:account.unknown_bank")}
              </p>
            )}
            <p
              className={clsx([
                "text-xl",
                {
                  italic: !currencyData?.name,
                },
              ])}
            >
              {currencyData?.name || t("pages:account.unknown_currency")}
            </p>
          </div>
        </div>

        <div className={clsx(["pr-0", "xl:pr-8"])}>
          <p className={clsx(["truncate", "text-base", "flex"])}>
            <span
              className={clsx([
                "inline-block",
                "relative",
                "font-normal",
                "truncate",
                "max-w-[70%]",
                "md:max-w-[90%]",
                "text-2xl",
              ])}
            >
              {name
                ? truncateWithEllipsis({
                    text: `${disabled ? "* " : ""}${name}`,
                    maxLen: 50,
                  })
                : "..."}
            </span>
          </p>
          <div
            className={clsx([
              "flex",
              "flex-row",
              "w-full",
              "justify-start",
              "pointer-events-none",
              "text-3xl",
              "font-medium",
            ])}
          >
            {formatCurrency({
              value: available,
              currency: currencyData?.symbol_native || "?",
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default memo(AutoBankCard);
